import { PaginationControls } from "../../common/PaginationControls.js";
import { PointService } from "../../../services/point.service.js";

export class WalletHistoryList {
    constructor(userId) {
        this.userId = userId;
        this.perPage = 10;
        this.pageSnapshots = [];
        this.currentPage = 1;
        this.container = null;
    }

    render() {
        return `
            <div id="wallet-history-section">
                <h3 class="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span class="material-symbols-outlined text-gray-400">receipt_long</span> ${window.locale.t('wallet.history.title')}
                </h3>
                
                <div id="wallet-history-list" class="space-y-1">
                    <div class="text-center py-8 text-gray-400">
                        <span class="material-symbols-outlined animate-spin">progress_activity</span>
                    </div>
                </div>

                <!-- Pagination -->
                <div id="wallet-pagination" class="flex justify-center gap-4 mt-6"></div>
            </div>
        `;
    }

    async init(parent) {
        this.container = parent.querySelector('#wallet-history-section');
        await this.loadPage(1);
    }

    async loadPage(page) {
        if (!this.container) return;

        const listContainer = this.container.querySelector('#wallet-history-list');
        const paginationContainer = this.container.querySelector('#wallet-pagination');

        // UI Loading
        listContainer.classList.add('opacity-50', 'pointer-events-none');

        const lastDoc = page > 1 ? this.pageSnapshots[page - 2] : null;
        const { items, lastVisible } = await PointService.getHistory(this.userId, this.perPage, lastDoc);

        if (items.length > 0) {
            this.pageSnapshots[page - 1] = lastVisible;
        }

        this.renderList(items, listContainer);
        listContainer.classList.remove('opacity-50', 'pointer-events-none');

        this.renderControls(items.length, page, paginationContainer);
    }

    // [HELPER] Localize Transaction Description (Hybrid Adapter)
    getLocalizedDescription(description) {
        // 1. Check if it matches a known Legacy String
        const legacyMap = {
            '작품 감상': 'point.reason.reading',
            '매일 출석 보상': 'point.reason.attendance',
            '웰컴 보상': 'point.reason.welcome',
            '친구 초대 보상': 'point.reason.referral',
            '오서 신청': 'point.reason.author_apply',
            '대여': 'point.reason.rental',
            '소장': 'point.reason.purchase',
            // English Legacy
            'Reading Reward': 'point.reason.reading',
            'Daily Attendance': 'point.reason.attendance'
        };

        if (legacyMap[description]) {
            return window.locale.t(legacyMap[description]);
        }

        // 2. Check if it is a Translation Key (Future Data)
        if (description && description.startsWith('point.reason.')) {
            return window.locale.t(description);
        }

        // 3. Fallback: Return original
        return description;
    }

    renderList(items, target) {
        if (!items || items.length === 0) {
            target.innerHTML = `
                <div class="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-400 text-sm">
                    ${window.locale.t('wallet.history.empty')}
                </div>
            `;
            return;
        }

        target.innerHTML = `
            <div class="bg-white border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-50">
                ${items.map(item => {
            const isPlus = item.type === 'earn';
            const colorClass = isPlus ? 'text-blue-600' : 'text-red-500';
            const icon = isPlus ? 'add_circle' : 'remove_circle';
            const date = item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'Just now';

            // Currency Label Logic
            let currencyText = '';
            if (item.currency === 'star') currencyText = 'STAR';
            else if (item.currency === 'activity') currencyText = 'ACTIVITY';
            else if (item.currency === 'mixed' || (!item.currency && item.breakdown)) {
                // Detailed Breakdown
                const b = item.breakdown || {};
                const act = Math.abs(b.activity || 0);
                const star = Math.abs(b.star || 0);
                currencyText = `${act} P + ${star} Star`;
            } else {
                // Fallback: check source to determine currency
                currencyText = (item.source === 'charge' || item.source === 'payment') ? 'STAR' : 'ACTIVITY';
            }

            // [FIX] Localize Description (with charge fallback)
            let displayDesc = this.getLocalizedDescription(item.description);
            if (!displayDesc || displayDesc === 'undefined') {
                if (item.source === 'charge' || item.source === 'payment') {
                    displayDesc = window.locale.t('point.reason.star_charge');
                } else {
                    displayDesc = item.source || window.locale.t('point.reason.other');
                }
            }

            return `
                        <div class="p-4 flex items-center justify-between">
                            <div class="flex items-center gap-3">
                                <div class="w-8 h-8 rounded-full ${isPlus ? 'bg-blue-50' : 'bg-red-50'} flex items-center justify-center ${colorClass}">
                                    <span class="material-symbols-outlined text-lg">${icon}</span>
                                </div>
                                <div>
                                    <p class="text-sm font-bold text-gray-900 line-clamp-1">${displayDesc}</p>
                                    <p class="text-xs text-gray-400">${date}</p>
                                </div>
                            </div>
                            <div class="text-right flex-shrink-0 ml-2">
                                <p class="font-bold ${colorClass}">${isPlus ? '+' : ''}${item.amount} P</p>
                                <p class="text-[10px] text-gray-400 font-mono">
                                    ${currencyText}
                                </p>
                            </div>
                        </div>
                    `;
        }).join('')}
            </div>
        `;
    }

    renderControls(itemCount, page, target) {
        const hasNext = itemCount === this.perPage;
        const hasPrev = page > 1;

        if (!hasNext && !hasPrev) {
            target.innerHTML = '';
            return;
        }

        target.innerHTML = `
            <button id="btn-wallet-prev" class="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" ${!hasPrev ? 'disabled' : ''}>
                <span class="material-symbols-outlined">west</span>
            </button>
            <span class="flex items-center font-bold text-gray-500">Page ${page}</span>
            <button id="btn-wallet-next" class="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" ${!hasNext ? 'disabled' : ''}>
                <span class="material-symbols-outlined">east</span>
            </button>
        `;

        if (hasPrev) {
            target.querySelector('#btn-wallet-prev').onclick = () => {
                this.loadPage(page - 1);
            };
        }
        if (hasNext) {
            target.querySelector('#btn-wallet-next').onclick = () => {
                this.loadPage(page + 1);
            };
        }
    }
}
